// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { INVOICE_PAYMENT_HISTORY_FILTER_KEYS, INVOICE_PAYMENT_HISTORY_SORT_KEYS } from "@eisinc/XDL/constants";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ApiParameter, HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import { Draft } from "immer";

type FilterKeys = keyof typeof INVOICE_PAYMENT_HISTORY_FILTER_KEYS;

type MappedFilterKeys = {
    [key in FilterKeys]?: string;
};

export interface GetInvoicePaymentHistoryApiParameter extends ApiParameter, HasPagingParameters, MappedFilterKeys {
    [key: string]: any;
}

export interface InvoicePaymentHistoryListState {
    getInvoicePaymentHistoryApiParameter: GetInvoicePaymentHistoryApiParameter;
    isFilterOpen: boolean;
}

const initialState: InvoicePaymentHistoryListState = {
    isFilterOpen: false,
    getInvoicePaymentHistoryApiParameter: {
        sort: `${INVOICE_PAYMENT_HISTORY_SORT_KEYS.PAYMENT_DATE} Desc`,
        customerSequence: "-1",
        pageSize: 16,
        page: 1,
    },
};

const reducer = {
    "Pages/InvoicePaymentHistoryListPage/ClearParameter": (draft: Draft<InvoicePaymentHistoryListState>) => {
        draft.getInvoicePaymentHistoryApiParameter = {
            ...initialState.getInvoicePaymentHistoryApiParameter,
            pageSize: draft.getInvoicePaymentHistoryApiParameter.pageSize,
        };
    },
    "Pages/InvoicePaymentHistoryListPage/ToggleFiltersOpen": (draft: Draft<InvoicePaymentHistoryListState>) => {
        draft.isFilterOpen = !draft.isFilterOpen;
    },
    "Pages/InvoicePaymentHistoryListPage/UpdateSearchFields": (
        draft: Draft<InvoicePaymentHistoryListState>,
        action: {
            parameter: GetInvoicePaymentHistoryApiParameter & UpdateSearchFieldsType;
        },
    ) => {
        const { type } = action.parameter;
        delete action.parameter.type;
        if (type === "Replace") {
            draft.getInvoicePaymentHistoryApiParameter = action.parameter;
        } else if (type === "Initialize") {
            draft.getInvoicePaymentHistoryApiParameter = {
                ...initialState.getInvoicePaymentHistoryApiParameter,
                ...action.parameter,
            };
        } else {
            draft.getInvoicePaymentHistoryApiParameter = {
                ...draft.getInvoicePaymentHistoryApiParameter,
                ...action.parameter,
            };

            for (const key in draft.getInvoicePaymentHistoryApiParameter) {
                const value = (<any>draft.getInvoicePaymentHistoryApiParameter)[key];

                // remove empty parameters
                if (value === "" || value === undefined) {
                    delete (<any>draft.getInvoicePaymentHistoryApiParameter)[key];
                }
            }

            for (const key in action.parameter) {
                // go back to page 1 if any other parameters changed
                if (
                    draft.getInvoicePaymentHistoryApiParameter.page &&
                    draft.getInvoicePaymentHistoryApiParameter.page > 1 &&
                    key !== "page" &&
                    key !== "pageSize"
                ) {
                    draft.getInvoicePaymentHistoryApiParameter.page = 1;
                }
            }
        }
    },
};

const xInvoicePaymentHistoryListState = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xInvoicePaymentHistoryListState = xInvoicePaymentHistoryListState;

export type xInvoicePaymentHistoryListState = {
    xInvoicePaymentHistoryListState: InvoicePaymentHistoryListState;
};

type xInvoicePaymentHistoryListStateReducers = {};

export type xInvoicePaymentHistoryListStateActions =
    | Parameters<xInvoicePaymentHistoryListStateReducers[keyof xInvoicePaymentHistoryListStateReducers]>[1]
    | AnyAction;
